import {
    ChangeDetectionStrategy,
    Component,
    computed,
    forwardRef,
    HostBinding,
    inject,
    InjectionToken,
    input,
    linkedSignal,
    output,
    signal,
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {TranslateService, Translation} from '@brunata-angular/util';

export interface PillOption<T = string> {
    value: T;
    /** Left out for an icon-only option, where `title` is the only name it carries. */
    label?: Translation;
    /** Appended to the label as ` (3)`, for a filter tab that counts what picking it would show. */
    count?: number;
    /** Bootstrap Icons classes, e.g. `bi bi-bar-chart`, drawn ahead of the label. */
    iconClass?: string;
    /** Tooltip on the option. */
    title?: Translation;
}

/**
 * `framed` draws the options inside one bordered row, each an equal share of the full width
 * (MyBrunata). `secondary` is Bootstrap's own button group with a secondary button per option, the
 * look the WebBill pages already use.
 */
export type PillGroupAppearance = 'framed' | 'secondary';

/**
 * App-wide pill look, set once per app: ngwebbill provides `secondary` so every group takes the
 * Bootstrap button-group look its pages already use. Leaving the token unprovided keeps the
 * `framed` look (MyBrunata). A subtree can override it with a component-level provider.
 */
export const PILL_GROUP_DEFAULTS = new InjectionToken<{appearance: PillGroupAppearance}>(
    'PILL_GROUP_DEFAULTS',
);

const FRAMED_LABEL_CLASSES =
    'btn d-flex align-items-center justify-content-center flex-fill gap-2 overflow-hidden text-truncate';

const SECONDARY_LABEL_CLASSES = 'btn btn-secondary';

interface Pill<T> {
    value: T;
    id: string;
    text: string | null;
    title: string | null;
    iconClass: string | null;
}

/**
 * A segmented toggle: a row of pill-shaped radio buttons where exactly one option is picked. Drive
 * it from a form control (`formControlName` / `[formControl]`) or, where the selection is a signal
 * rather than form state, from `[value]` plus `(valueChange)`.
 */
@Component({
    selector: 'app-pill-group',
    standalone: true,
    templateUrl: './pill-group.component.html',
    styleUrl: './pill-group.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PillGroupComponent),
            multi: true,
        },
    ],
})
export class PillGroupComponent<T = string> implements ControlValueAccessor {
    readonly options = input<ReadonlyArray<PillOption<T>>>([]);
    /** Names the radio group, so two groups on one page do not pick for each other. */
    readonly groupName = input('pillGroup');
    /** The option to check, for a signal-driven group. A form control writes through `writeValue`. */
    readonly value = input<T | null>(null);
    /** The option the user picked. Not emitted for a programmatic write. */
    readonly valueChange = output<T>();

    /** The one rendered selection: seeded by `value`, replaced by a form write or a user pick. */
    protected readonly selected = linkedSignal(() => this.value());

    private readonly appearance: PillGroupAppearance =
        inject(PILL_GROUP_DEFAULTS, {optional: true})?.appearance ?? 'framed';

    /** The framed look is drawn from the host, so it can size the group as well as the options. */
    @HostBinding('class.pill-group-framed')
    protected readonly framed = this.appearance === 'framed';

    protected readonly labelClass = this.framed ? FRAMED_LABEL_CLASSES : SECONDARY_LABEL_CLASSES;
    protected readonly disabled = signal(false);

    private readonly translateService = inject(TranslateService);

    protected readonly pills = computed<Pill<T>[]>(() =>
        this.options().map(option => ({
            value: option.value,
            id: `${this.groupName()}_${String(option.value)}`,
            text: this.toText(option),
            title: option.title == null ? null : this.translate(option.title),
            iconClass: option.iconClass ?? null,
        })),
    );

    private onChange: (value: T | null) => void = () => {};
    private onTouched: () => void = () => {};

    writeValue(value: T | null): void {
        this.selected.set(value);
    }

    registerOnChange(fn: (value: T | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(disabled: boolean): void {
        this.disabled.set(disabled);
    }

    protected select(value: T): void {
        this.selected.set(value);
        this.valueChange.emit(value);
        this.onChange(value);
        this.onTouched();
    }

    private toText(option: PillOption<T>): string | null {
        if (option.label == null) {
            return null;
        }
        const label = this.translate(option.label);
        return option.count == null ? label : `${label} (${option.count})`;
    }

    private translate(translation: Translation): string {
        return this.translateService.translate(
            translation.key,
            translation.application,
            translation.module,
        );
    }
}
